<script>
import UserObject from '@/components/page-user-edit/UserObject.vue';
import UserFilters from '@/components/page-user-edit/UserFilters.vue';
import PaginationController from '@/components/base-components/PaginationController.vue';

export default {
    components: {
        UserObject,
        UserFilters,
        PaginationController,
    },
    data() {
        return {
            sortColumns: {
                idSort: { field: 'id', direction: 'desc' },
                roleSort: { field: 'user_role_id', direction: null },
                nameSort: { field: 'name', direction: null },
                emailSort: { field: 'email', direction: null },
                companySort: { field: 'company', direction: null },
                createdAtSort: { field: 'created_at', direction: null },
                downloadsSort: {
                    field: 'total_downloaded_images',
                    direction: null,
                },
            },
        };
    },
    computed: {
        users() {
            return this.$store.getters.USERS;
        },
        isLoading() {
            return this.$store.getters.IS_USERS_LOADING;
        },
        pagination() {
            return this.$store.getters.USER_PAGINATION;
        },
        currentPage() {
            return this.$store.getters.USER_CURRENT_PAGE;
        },
        totalPages() {
            return this.$store.getters.USER_TOTAL_PAGES;
        },
        maxPage() {
            return this.totalPages - 1; // PaginationController uses 0-based index
        },
        displayRange() {
            const start = this.pagination.offset + 1;
            const end = Math.min(
                this.pagination.offset + this.pagination.count,
                this.pagination.total
            );
            return `${start}-${end} of ${this.pagination.total}`;
        },
    },
    methods: {
        handleSort(columnId) {
            const column = this.sortColumns[columnId];
            if (!column) return;

            // Cycle through: null -> asc -> desc -> null
            if (column.direction === null) {
                column.direction = 'asc';
            } else if (column.direction === 'asc') {
                column.direction = 'desc';
            } else {
                column.direction = null;
            }

            // Reset other columns (single-column sort)
            Object.keys(this.sortColumns).forEach((key) => {
                if (key !== columnId) {
                    this.sortColumns[key].direction = null;
                }
            });

            // Build sort string
            const sortString = column.direction
                ? `${column.field}:${column.direction}`
                : 'id:desc'; // Default sort

            this.$store.commit('SET_USER_SORT', sortString);
            this.$store.commit('SET_USER_OFFSET', 0); // Reset to first page
            this.$store.dispatch('loadUsers');
        },

        getSortDirection(columnId) {
            return this.sortColumns[columnId]?.direction || null;
        },

        async changeLimit(event) {
            const newLimit = parseInt(event.target.value);
            this.$store.commit('SET_USER_LIMIT', newLimit);
            await this.$store.dispatch('loadUsers');
        },

        async handlePageChange(action) {
            await this.$store.dispatch('changeUserPage', action);
        },
    },
};
</script>

<template>
    <div class="user_list_container">
        <div class="right_header">
            <label>Users ({{ pagination.total }})</label>
        </div>

        <UserFilters />

        <div v-if="isLoading" class="loading_container">
            <p>Loading users...</p>
        </div>

        <div v-else-if="users.length === 0" class="empty_container">
            <p>No users found</p>
        </div>

        <div v-else>
            <div class="table_scroll_container">
                <table>
                    <thead>
                    <tr>
                        <th
                            @click="handleSort('idSort')"
                            :class="{
                                sortable: true,
                                sorted: getSortDirection('idSort') !== null,
                            }"
                        >
                            ID
                            <span
                                v-if="getSortDirection('idSort') === 'asc'"
                                class="sort_indicator"
                                >▲</span
                            >
                            <span
                                v-if="getSortDirection('idSort') === 'desc'"
                                class="sort_indicator"
                                >▼</span
                            >
                        </th>
                        <th>Status</th>
                        <th
                            @click="handleSort('roleSort')"
                            :class="{
                                sortable: true,
                                sorted: getSortDirection('roleSort') !== null,
                            }"
                        >
                            Role
                            <span
                                v-if="getSortDirection('roleSort') === 'asc'"
                                class="sort_indicator"
                                >▲</span
                            >
                            <span
                                v-if="getSortDirection('roleSort') === 'desc'"
                                class="sort_indicator"
                                >▼</span
                            >
                        </th>
                        <th
                            @click="handleSort('nameSort')"
                            :class="{
                                sortable: true,
                                sorted: getSortDirection('nameSort') !== null,
                            }"
                        >
                            Name
                            <span
                                v-if="getSortDirection('nameSort') === 'asc'"
                                class="sort_indicator"
                                >▲</span
                            >
                            <span
                                v-if="getSortDirection('nameSort') === 'desc'"
                                class="sort_indicator"
                                >▼</span
                            >
                        </th>
                        <th
                            @click="handleSort('emailSort')"
                            :class="{
                                sortable: true,
                                sorted: getSortDirection('emailSort') !== null,
                            }"
                        >
                            Email
                            <span
                                v-if="getSortDirection('emailSort') === 'asc'"
                                class="sort_indicator"
                                >▲</span
                            >
                            <span
                                v-if="getSortDirection('emailSort') === 'desc'"
                                class="sort_indicator"
                                >▼</span
                            >
                        </th>
                        <th
                            @click="handleSort('companySort')"
                            :class="{
                                sortable: true,
                                sorted:
                                    getSortDirection('companySort') !== null,
                            }"
                        >
                            Company
                            <span
                                v-if="getSortDirection('companySort') === 'asc'"
                                class="sort_indicator"
                                >▲</span
                            >
                            <span
                                v-if="
                                    getSortDirection('companySort') === 'desc'
                                "
                                class="sort_indicator"
                                >▼</span
                            >
                        </th>
                        <th
                            @click="handleSort('createdAtSort')"
                            :class="{
                                sortable: true,
                                sorted:
                                    getSortDirection('createdAtSort') !== null,
                            }"
                        >
                            Created At
                            <span
                                v-if="
                                    getSortDirection('createdAtSort') === 'asc'
                                "
                                class="sort_indicator"
                                >▲</span
                            >
                            <span
                                v-if="
                                    getSortDirection('createdAtSort') === 'desc'
                                "
                                class="sort_indicator"
                                >▼</span
                            >
                        </th>
                        <th
                            @click="handleSort('downloadsSort')"
                            :class="{
                                sortable: true,
                                sorted:
                                    getSortDirection('downloadsSort') !== null,
                            }"
                        >
                            Downloads
                            <span
                                v-if="
                                    getSortDirection('downloadsSort') === 'asc'
                                "
                                class="sort_indicator"
                                >▲</span
                            >
                            <span
                                v-if="
                                    getSortDirection('downloadsSort') === 'desc'
                                "
                                class="sort_indicator"
                                >▼</span
                            >
                        </th>
                        <th class="th_center th_150">
                            Subscription
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <UserObject
                        v-for="user in users"
                        :key="user.id"
                        :user="user"
                    />
                </tbody>
            </table>
            </div>

            <div class="pagination_container">
                <div class="pagination_info">
                    <span>Showing {{ displayRange }} users</span>

                    <div class="items_per_page">
                        <label>Items per page:</label>
                        <select
                            :value="pagination.limit"
                            @change="changeLimit"
                            :disabled="isLoading"
                        >
                            <option :value="10">10</option>
                            <option :value="50">50</option>
                            <option :value="100">100</option>
                            <option :value="500">500</option>
                        </select>
                    </div>
                </div>

                <PaginationController
                    :currentPage="currentPage - 1"
                    :maxPage="maxPage"
                    @first="handlePageChange('first')"
                    @back="handlePageChange('prev')"
                    @next="handlePageChange('next')"
                    @last="handlePageChange('last')"
                />
            </div>
        </div>
    </div>
</template>

<style scoped>
.user_list_container {
    padding: 20px;
}

.right_header {
    margin-bottom: 20px;
    font-size: 24px;
    font-weight: 600;
    color: var(--text-color);
}

.loading_container,
.empty_container {
    padding: 40px 20px;
    text-align: center;
    color: var(--text-color-secondary);
}

.table_scroll_container {
    overflow-x: auto;
    overflow-y: visible;
    margin: 20px 0;
    border: 1px solid var(--border-color);
    border-radius: 8px;
}

table {
    width: 100%;
    min-width: 1200px;
    border-collapse: collapse;
}

th {
    background: var(--background-hover);
    padding: 12px 15px;
    text-align: left;
    font-weight: 600;
    color: var(--text-color);
    border-bottom: 2px solid var(--border-color);
}

th.sortable {
    cursor: pointer;
    user-select: none;
    transition: background 0.2s ease;
}

th.sortable:hover {
    background: var(--primary-color);
    color: white;
}

th.sorted {
    background: var(--primary-color);
    color: white;
}

.sort_indicator {
    margin-left: 5px;
    font-size: 10px;
}

.th_center {
    text-align: center;
}

.th_150 {
    width: 150px;
    min-width: 150px;
}

.pagination_container {
    margin-top: 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 15px;
}

.pagination_info {
    display: flex;
    align-items: center;
    gap: 20px;
    color: var(--text-color);
}

.items_per_page {
    display: flex;
    align-items: center;
    gap: 8px;
}

.items_per_page label {
    font-size: 14px;
    color: var(--text-color);
}

.items_per_page select {
    padding: 6px 10px;
    border: 1px solid var(--border-color);
    border-radius: 4px;
    background: var(--background-color);
    color: var(--text-color);
    cursor: pointer;
}

.items_per_page select:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}
</style>
